/**
 * 인증 서비스
 * 네이티브 소셜 로그인(Google/Apple)을 통한 인증을 제공합니다.
 */

import type { Session } from "@supabase/supabase-js";
import { Capacitor } from "@capacitor/core";
import { supabase } from "./supabase";
import { clearToken } from "./tokenBridge";
import type {
  AuthState,
  AuthUser,
  ServiceResult,
} from "./types";

/** 소셜 로그인 프로바이더 */
export type SocialProvider = 'google' | 'apple';

/** Supabase User를 AuthUser로 변환 */
function toAuthUser(user: { id: string; email?: string; created_at: string }): AuthUser {
  return {
    id: user.id,
    email: user.email ?? "",
    createdAt: user.created_at,
  };
}

/**
 * 네이티브 소셜 로그인 (Google/Apple)
 * 네이티브 플랫폼: @capgo/capacitor-social-login으로 네이티브 다이얼로그 → ID token → Supabase 인증
 * 웹 플랫폼: supabase.auth.signInWithOAuth() 브라우저 기반 폴백 (개발 환경용)
 */
export async function signInWithSocialProvider(
  provider: SocialProvider
): Promise<ServiceResult<AuthUser>> {
  // 웹 플랫폼: 브라우저 기반 OAuth 폴백 (개발 환경용)
  if (!Capacitor.isNativePlatform()) {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      return {
        success: false,
        error: { code: 'UNAUTHORIZED', message: error.message },
      };
    }
    // 브라우저 리다이렉트 후 onAuthStateChange에서 처리
    return { success: true, data: {} as AuthUser };
  }

  // 네이티브 플랫폼: @capgo/capacitor-social-login
  try {
    const { SocialLogin } = await import('@capgo/capacitor-social-login');

    const loginResult = await SocialLogin.login({
      provider,
      options: {
        scopes: provider === 'google' ? ['email', 'profile'] : ['email', 'name'],
      },
    });

    const result = loginResult.result;
    const idToken = result && 'idToken' in result ? result.idToken : null;
    if (!idToken) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: 'ID token을 가져올 수 없습니다.',
        },
      };
    }

    const { data, error } = await supabase.auth.signInWithIdToken({
      provider,
      token: idToken,
    });

    if (error || !data.user) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: error?.message ?? '로그인에 실패했습니다.',
        },
      };
    }

    return {
      success: true,
      data: toAuthUser(data.user),
    };
  } catch (err) {
    // 사용자가 로그인 취소한 경우 등
    const message = err instanceof Error ? err.message : '로그인 중 오류가 발생했습니다.';
    return {
      success: false,
      error: {
        code: 'UNAUTHORIZED',
        message,
      },
    };
  }
}

/** 로그아웃 */
export async function signOut(): Promise<ServiceResult<void>> {
  // 네이티브: RevenueCat 사용자 연결 해제 (실패해도 signOut 진행)
  if (Capacitor.isNativePlatform()) {
    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      await Purchases.logOut();
    } catch (e) {
      console.error('[auth.service] RevenueCat logOut 실패:', e);
    }
  }

  const { error } = await supabase.auth.signOut();

  if (error) {
    return {
      success: false,
      error: {
        code: "UNKNOWN_ERROR",
        message: error.message,
        details: error,
      },
    };
  }

  // iOS에서 Keychain 토큰 명시적 삭제
  // (onAuthStateChange에서도 삭제되지만, 즉시 삭제를 보장하기 위해)
  if (Capacitor.getPlatform() === 'ios') {
    try {
      await clearToken();
    } catch (error) {
      console.error('[auth.service] 토큰 삭제 실패:', error);
    }
  }

  return {
    success: true,
    data: undefined,
  };
}

/** 현재 세션 조회 */
export async function getSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/** 현재 로그인된 사용자 조회 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  const { data } = await supabase.auth.getUser();

  if (!data.user) {
    return null;
  }

  return toAuthUser(data.user);
}

/**
 * 인증 상태 변경 구독
 * @param callback - 상태 변경 시 호출될 콜백
 * @returns 구독 해제 함수
 */
export function onAuthStateChange(
  callback: (user: AuthUser | null) => void
): () => void {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    if (session?.user) {
      callback(toAuthUser(session.user));
    } else {
      callback(null);
    }
  });

  return () => {
    data.subscription.unsubscribe();
  };
}

/** 앱 시작 시 인증 상태 초기화 */
export async function initializeAuth(): Promise<AuthState> {
  const { data, error } = await supabase.auth.getSession();

  if (error || !data.session) {
    return {
      isAuthenticated: false,
      user: null,
      isLoading: false,
    };
  }

  return {
    isAuthenticated: true,
    user: toAuthUser(data.session.user),
    isLoading: false,
  };
}
